// src/components/RecipeForm.tsx
import React, { useState, useEffect } from 'react';
import fetchWithAuth from '../utils/fetchWithAuth';
import IngredientRow from './Shared/IngredientRow';
import type { Ingredient } from './Shared/IngredientRow';
import FileUploader from './FileUploader';
import UnverifiedIngredientsModal from './UnverifiedIngredientsModal';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import TagEditor, { Tag } from './ui/molecules/TagEditor';
import BackButton from '../components/ui/atoms/BackButton';
import type { Unit } from './Shared/UnitAutocomplete';
import { v4 as uuidv4 } from 'uuid';
import SectionHeader from './ui/atoms/SectionTitle/SectionHeader';
import UnderlineInput from './Shared/UnderlineInput';
import { Button } from '../components/ui/atoms/Button/Button';
import { QuantitySelector } from './ui/atoms/QuantitySelector/QuantitySelector';
import { HiPlusSm } from 'react-icons/hi';

type RecipeFormProps = {
  initialData?: any;
  isEditing?: boolean;
  onSuccess?: () => void;
};

const RecipeForm: React.FC<RecipeFormProps> = ({
  initialData = null,
  isEditing = false,
  onSuccess,
}) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [duration, setDuration] = useState(initialData?.duration || '');
  const [servings, setServings] = useState(initialData?.servings || 1);
  const [ingredients, setIngredients] = useState<Ingredient[]>(
    initialData?.ingredients?.map((i: any) => ({
      id: uuidv4(),
      name: i.name || i.catalog.nameCanonical,
      catalogId: i.catalogId ?? null, 
      isVerified: i.catalog.isVerified,
      quantity: i.quantity,
      unitId: i.unitId,
      scalable: i.scalable,
      unitLabel: '',
    })) || [{
      id: uuidv4(),
      name: '',
      catalogId: null,
      isVerified: false,       // ← new
      quantity: '',
      unitId: '',
      unitLabel: '',
    }]
  );
  const [instructions, setInstructions] = useState<string[]>(
    initialData?.instructions || ['']
  );
  const [imageUrl, setImageUrl] = useState(initialData?.imageUrl || '');
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(
    initialData?.recipeTags?.map((rt: any) => rt.tag.id) || []
  );
  const [units, setUnits] = useState<Unit[]>([]);
  const [allowedUnitsByCatalog, setAllowedUnitsByCatalog] = useState<Record<string, string[]>>({});
  const [showUnverifiedModal, setShowUnverifiedModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useToast() as any;
  const navigate = useNavigate();
  const fallbackRecipeImage = '/no-image.png';

  // load tags, units, and seed ingredient list
  useEffect(() => {
    async function load() {
      const [tagsRes, unitsRes] = await Promise.all([
        fetchWithAuth(`/api/tags`),
        fetchWithAuth(`/api/units`),
      ]);
      const rawUnits = await unitsRes;
      const formattedUnits: Unit[] = rawUnits.map((u: any) => ({
        id: u.id,
        label: u.name,
        short: u.abbreviation || u.name,
      }));
      setUnits(formattedUnits);

      if (isEditing && initialData?.ingredients) {
        setIngredients(
          initialData.ingredients.map((i: any) => ({
            id: uuidv4(),
            name: i.name || i.catalog.nameCanonical,
            catalogId: i.catalogId ?? null,
            isVerified: i.catalog.isVerified,
            quantity: i.quantity,
            unitId: i.unitId,
            scalable: i.scalable,
            // look up the human-readable label for that unit
            unitLabel:
              formattedUnits.find((u) => u.id === i.unitId)?.label || '',
          }))
        );
      }

      const { manualTags } = await tagsRes;
      const allTags = Object.values(manualTags).flat() as Tag[];
      setTags(allTags);
    }
    load();
  }, []);

  // prefill allowedUnitsByCatalog when editing
  useEffect(() => {
    ingredients.forEach((ing) => {
      if (!ing.catalogId) return;
      const key = String(ing.catalogId);
      if (allowedUnitsByCatalog[key]) return;
      fetchWithAuth(`/api/unitConversions?catalogId=${ing.catalogId}`)
        .then((convs: { catalogId: number; unitId: string }[]) => {
          setAllowedUnitsByCatalog((prev) => ({
            ...prev,
            [key]: convs.map((c) => c.unitId),
          }));
        })
        .catch(console.error);
    });
  }, [ingredients, allowedUnitsByCatalog]);

  // Submit or update
  const doSubmit = async (payload: any) => {
    const url = isEditing
      ? `/api/recipes/${initialData.id}`
      : `/api/recipes`;
    const method = isEditing ? 'PATCH' : 'POST';
    await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    showToast({
      message: isEditing ? 'Recipe updated!' : 'Recipe created!',
      type: 'success',
    });
    if (onSuccess) onSuccess();
    else navigate('/app/profile');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    // ==== Validation guard ====
    if (!title.trim() || !description.trim()) {
      setIsSubmitting(false);
      showToast({ message: 'Title and description are required.', type: 'error' });
      return;
    }
    if (ingredients.some((i) => !i.name.trim())) {
      setIsSubmitting(false);
      showToast({ message: 'All ingredients must have a name.', type: 'error' });
      return;
    }
    if (instructions.some((i) => !i.trim())) {
      setIsSubmitting(false);
      showToast({ message: 'All instructions must have content.', type: 'error' });
      return;
    }
    // ========================
    const payload = {
      title,
      description,
      duration: Number(duration),
      servings,
      imageUrl,
      tags: selectedTags,
      ingredients: ingredients.map((i) => ({
        catalogId: i.catalogId,
        name: i.name,
        quantity: i.quantity,
        unitId: i.unitId,
        scalable: i.scalable,
      })),
      instructions,
    };

    const unverifiedCount = ingredients.filter((i) => !i.isVerified).length;
    if (unverifiedCount > 0 && !showUnverifiedModal) {
      setPendingPayload(payload);
      setShowUnverifiedModal(true);
      setIsSubmitting(false);
      return;
    }

    try {
      await doSubmit(payload);
    } catch {
      showToast({
        message: isEditing ? 'Error updating recipe.' : 'Error saving recipe.',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white pb-20 px-2">
      {/* fixed header with Back + Submit/Update */}
      <div className="fixed top-0 inset-x-0 z-50 bg-white h-[70px] flex items-center justify-between px-2">
        <BackButton onClick={() => navigate(-1)} />
        <Button
          type="submit"
          form="recipe-form"
          variant="header"
          disabled={isSubmitting}
          className="ml-auto"
        >
          {isSubmitting
            ? isEditing
              ? 'Updating...'
              : 'Submitting...'
            : isEditing
            ? 'Update Recipe'
            : 'Submit Recipe'}
        </Button>
      </div>

      {showUnverifiedModal && (
        <UnverifiedIngredientsModal
          count={ingredients.filter((i) => !i.isVerified).length}
          onCancel={() => setShowUnverifiedModal(false)}
          onConfirm={() => {
            setShowUnverifiedModal(false);
            doSubmit(pendingPayload);
          }}
        />
      )}

      <form onSubmit={handleSubmit} id="recipe-form" className="pt-[70px] space-y-6">
        {/* === MAIN INFO === */}
        <SectionHeader>Main Info</SectionHeader>

        <div>
          <label htmlFor="recipe-title" className="block text-sm font-medium text-gray-700 mb-1">
            Recipe Title
          </label>
          <UnderlineInput
            id="recipe-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter recipe title"
          />
        </div>

        <div>
          <label htmlFor="recipe-description" className="block text-sm font-medium text-gray-700 mb-1">
            Short Description
          </label>
          <UnderlineInput
            id="recipe-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a brief description"
          />
        </div>

        <div>
          <label htmlFor="recipe-duration" className="block text-sm font-medium text-gray-700 mb-1">
            Duration (minutes)
          </label>
          <UnderlineInput
            id="recipe-duration"
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g. 30"
          />
        </div>

        {/* === IMAGE === */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 relative">
            <img
              src={imageUrl || fallbackRecipeImage}
              alt={title || 'Recipe'}
              className="w-16 h-16 rounded object-cover border"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = fallbackRecipeImage;
              }}
            />
            {imageUrl && (
              <button
                type="button"
                onClick={() => setImageUrl('')}
                className="absolute top-[-6px] right-[-6px] bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-black rounded-full w-5 h-5 text-xs flex items-center justify-center shadow-sm transition"
                aria-label="Remove image"
                title="Remove image"
              >
                &times;
              </button>
            )}
          </div>
          <FileUploader
            label={imageUrl ? 'Replace Recipe Image' : 'Upload Recipe Image'}
            onUpload={(url) => setImageUrl(url)}
            type="recipe"
          />
        </div>

        {/* === TAGS === */}
        <SectionHeader>Tags</SectionHeader>
        <TagEditor allTags={tags} selectedTags={selectedTags} toggleTag={(id) => {
          setSelectedTags((prev) =>
            prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
          );
        }} />

        {/* === INGREDIENTS === */}
        <div>
          <SectionHeader>Ingredients</SectionHeader>
          <QuantitySelector
            amount={servings}
            title="Servings:"
            decrease={() => setServings(Math.max(1, servings - 1))}
            increase={() => setServings(servings + 1)}
          />
          {ingredients.map((ing, idx) => {
            const allowed = allowedUnitsByCatalog[String(ing.catalogId)] || [];
            const ingredientUnits = ing.catalogId
              ? units.filter((u) => allowed.includes(u.id))
              : units;
            return (
              <IngredientRow
                key={ing.id}
                ingredient={ing}
                units={ingredientUnits}
                onChange={(updated) =>
                  setIngredients((prev) =>
                    prev.map((x) => (x.id === ing.id ? updated : x))
                  )
                }
                onRemove={() =>
                  setIngredients((prev) =>
                    prev.filter((x) => x.id !== ing.id)
                  )
                }
              />
            );
          })}
          <Button variant="underline" onClick={() =>
            setIngredients([
              ...ingredients,
              { id: uuidv4(), name: '', catalogId: null, isVerified: false, quantity: '', unitId: '', unitLabel: '' },
            ])
          }>
            <HiPlusSm className="w-5 h-5" /> Add Ingredient
          </Button>
        </div>

        {/* === INSTRUCTIONS === */}
        <div>
        <SectionHeader>Instructions</SectionHeader>
          {instructions.map((step, i) => (
            <div key={i} className="space-y-1 mb-6">
              <label htmlFor={`instruction-${i}`} className="block text-sm font-medium text-gray-700">
                Step {i + 1}
              </label>
              <div className="flex items-start space-x-2">
                <textarea
                  id={`instruction-${i}`}
                  value={step}
                  onChange={(e) => {
                    const arr = [...instructions];
                    arr[i] = e.target.value;
                    setInstructions(arr);
                  }}
                  className="w-full border-b border-gray-300 focus:outline-none px-1 py-1 bg-transparent"
                  placeholder={`Describe step ${i + 1}`}
                />
                <Button variant="remove" onClick={() =>
                  setInstructions((prev) => prev.filter((_, idx) => idx !== i))
                } />
              </div>
            </div>
          ))}
          <Button variant="underline" onClick={() => setInstructions([...instructions, ''])}>
            <HiPlusSm className="w-5 h-5" /> Add Instruction Step
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RecipeForm;
